import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export function useShippingCredentials(storeId: string | null) {
  return useQuery({
    queryKey: ["shipping-credentials", storeId],
    queryFn: () => fetchJson<any[]>(`/api/shipping/credentials?storeId=${storeId}`),
    enabled: !!storeId,
  });
}

/** Aktivni nalozi kroz sve trgovine — veleprodajna narudžba nije vezana za trgovinu. */
export function useActiveShippingCredentials(enabled = true) {
  return useQuery({
    queryKey: ["shipping-credentials", "active"],
    queryFn: () => fetchJson<any[]>("/api/shipping/credentials/active"),
    enabled,
  });
}

export function useAllShippingCredentials(search: string, page: number, limit: number) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.set("search", search);
  return useQuery({
    queryKey: ["shipping-credentials", "all", search, page, limit],
    queryFn: () => fetchJson<any>(`/api/shipping/credentials/all?${params}`),
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) =>
      fetchJson("/api/shipping/credentials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping-credentials"] }),
  });
}

export function useUpdateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: any) =>
      fetchJson(`/api/shipping/credentials/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping-credentials"] }),
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/shipping/credentials/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping-credentials"] }),
  });
}

export function useBulkImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) =>
      fetchJson("/api/shipping/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["shipping-credentials"] });
      qc.invalidateQueries({ queryKey: ["order-shipments-bulk"] });
    },
  });
}

export function useResetShipping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { orderIds: string[]; storeId: string }) =>
      fetchJson("/api/shipping/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order-shipments-bulk"] });
    },
  });
}

export type ShipmentSummary = {
  trackingNumber: string | null;
  status: string;
  createdAt: number;
  credentialName: string | null;
  credentialColor: string | null;
};

export function useShipmentsBulk(storeId: string | null, orderIds: (string | number)[]) {
  const ids = orderIds.map(String).sort();
  return useQuery({
    queryKey: ["order-shipments-bulk", storeId, ids],
    queryFn: () =>
      fetchJson<Record<string, ShipmentSummary>>(
        `/api/shipping/shipments/bulk?storeId=${storeId}&orderIds=${ids.join(",")}`,
      ),
    enabled: !!storeId && ids.length > 0,
  });
}

export function useOrderShipments(orderId: string | null, storeId: string | null) {
  return useQuery({
    queryKey: ["order-shipments", orderId, storeId],
    queryFn: () => fetchJson<any[]>(`/api/shipping/shipments/${orderId}?storeId=${storeId}`),
    enabled: !!orderId && !!storeId,
  });
}
